import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { Alert, Box, Card, Icon, Text, Title } from '@nimbus-ds/components';
import { FileAltIcon, FileIcon, IdCardIcon } from '@nimbus-ds/icons';
import AppLayout from 'App/components/AppLayout';
import { FormattedMultilineText } from 'commons/components';
import DocumentUploader from 'commons/components/DocumentUploader';
import { useBankAccount, useToast } from 'commons/hooks';
import {
  formatCuit,
  getLegalEntityType,
  useTranslationWithPrefix,
} from 'commons/utils';
import { DocumentsContext } from '../../DocumentsContext';
import FooterButtons from '../FooterButtons';
import { useIdValidationRoutes } from '../hooks';

function AdditionalDocumentation(): JSX.Element {
  const { t } = useTranslationWithPrefix(
    'identityValidation.additionalDocumentation',
  );
  const { t: tParent } = useTranslationWithPrefix('identityValidation');
  const history = useHistory();
  const { goToNextRoute } = useIdValidationRoutes();
  const bankAccount = useBankAccount();
  const { showToastError } = useToast();
  const documents = useContext(DocumentsContext);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [errorText, setErrorText] = useState({ show: false, text: '' });

  useEffect(() => {
    if (documents.powerOfAttorney) {
      setUploadCompleted(true);
    }
  }, [documents.powerOfAttorney]);

  const handleSubmit = () => {
    track('pago-nube-prueba-de-vida-poder-continue-click', {
      has_uploaded_document: uploadCompleted,
      legal_entity_type: getLegalEntityType(bankAccount.data?.fiscalDocument),
    });
    if (uploadCompleted) {
      goToNextRoute();
    } else {
      showToastError(tParent('errors.uploadOneFile'));
    }
  };

  const handleOnSuccess = () => {
    setUploadCompleted(true);
    setErrorText({ show: false, text: '' });
  };

  const handleOnError = (text: string) => {
    setUploadCompleted(false);
    setErrorText({ show: text.length > 0, text });
  };

  const handleOnDelete = () => {
    setUploadCompleted(false);
  };

  return (
    <AppLayout
      title={tParent('title')}
      footer={
        <FooterButtons
          onCancelClick={history.goBack}
          onSubmitClick={handleSubmit}
        />
      }
    >
      <Box display="flex" flexDirection="column" gap="4">
        <Text>{t('description')}</Text>

        {bankAccount.data?.fiscalDocument && (
          <Box display="flex" flexDirection="row" gap="2">
            <Icon source={<IdCardIcon />} color="primary-interactive" />
            <FormattedMultilineText formattedFontWeight="bold">
              {tParent('cuitOrCuil', {
                document: formatCuit(bankAccount.data.fiscalDocument),
              })}
            </FormattedMultilineText>
          </Box>
        )}

        <Card>
          <Card.Header>
            <Title as="h4">{t('cardTitle')}</Title>
          </Card.Header>
          <Box display="flex" flexDirection="column" gap="4">
            <Text fontWeight="medium">{t('documentsSubtitle')}</Text>

            <Box display="flex" flexDirection="column" gap="3">
              <Box
                display="flex"
                flexDirection="row"
                gap="2"
                alignItems="center"
              >
                <Icon
                  source={<FileAltIcon size="medium" />}
                  color="primary-interactive"
                />
                <Text>{t('documents.powerOfAttorney')}</Text>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                gap="2"
                alignItems="center"
              >
                <Icon
                  source={<FileAltIcon size="medium" />}
                  color="primary-interactive"
                />
                <Text>{t('documents.shareholderComposition')}</Text>
              </Box>
            </Box>

            <Box display="flex" flexDirection="row" gap="2" alignItems="center">
              <Icon
                source={<FileIcon size="medium" />}
                color="primary-interactive"
              />
              <FormattedMultilineText formattedFontWeight="bold">
                {t('upload.documentFormat')}
              </FormattedMultilineText>
            </Box>

            <Alert
              show={errorText.show}
              appearance="danger"
              title={t('upload.errorTitle')}
              onRemove={() => setErrorText({ show: false, text: '' })}
            >
              {errorText.text}
            </Alert>

            <Box display="flex" maxWidth="400px">
              <DocumentUploader
                aspectRatio="2/1"
                documentName="powerOfAttorney"
                onSuccess={handleOnSuccess}
                onError={handleOnError}
                ariaLabel="power-of-attorney"
                helperText={t('upload.chooseFile')}
                accept="image/jpeg,image/png,application/pdf"
                initialThumbnailFile={documents.powerOfAttorney}
                onDelete={handleOnDelete}
                autoUpload={true}
                errorEventName="pago-nube-prueba-de-vida-documento-error"
              />
            </Box>
          </Box>
        </Card>
      </Box>
    </AppLayout>
  );
}

export default AdditionalDocumentation;
